<template>
  <b-card class="full-funnel-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">CRM Full Funnel</h6>
    </template>
    <b-card-body>
      <apexchart type="bar" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'FullFunnelChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          stackType: '100%',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: [], // Stage names will go here
        },
        yaxis: {
          title: {
            text: 'Percentage of Leads',
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'left',
          offsetX: 40,
        },
      },
      series: [],
    };
  },
  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: 'Leads', data: [] }];
    },
  },
  watch: {
    dashboardData: { deep: true, handler(v) { if (v && v.full_funnel_chart != null && hasRight(this.user, 'dashboard', 'view')) this.processChartData(v.full_funnel_chart); } },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
          return;
        }
        if (this.dashboardData && this.dashboardData.full_funnel_chart != null) { this.processChartData(this.dashboardData.full_funnel_chart); return; }
        this.fetchFullFunnelChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
            return;
          }
          this.fetchFullFunnelChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchFullFunnelChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.full_funnel_chart != null)) {
        if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/full-funnel-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching full funnel chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || data.length === 0) {
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        this.series = [];
        return;
      }

      const categories = data.map(item => item.stage);
      // For a stacked 100% bar chart representing a funnel, each stage would ideally be a series,
      // but the data structure here is count per stage, which is more suited for a simple bar chart.
      // If the intent is to show progression, a waterfall or funnel specific chart might be better.
      // For now, I will display it as a simple horizontal bar chart showing count per stage.

      this.chartOptions = {
        ...this.chartOptions,
        chart: { ...this.chartOptions.chart, stacked: false, stackType: undefined }, // Change to non-stacked bar
        xaxis: {
          categories: categories,
        },
        yaxis: {
          title: {
            text: 'Number of Leads',
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true, // Different colors for each bar
          },
        },
        legend: { show: false }, // No legend needed for single series with distributed colors
      };

      this.series = [{
        name: 'Leads',
        data: data.map(item => item.count),
      }];
    },
  },
};
</script>

<style scoped>
.full-funnel-chart {
  margin-top: 20px;
}
</style>